import { ReactNode } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../../../shared/ui/header/LanguageSelector";
import { DIRECTORY_PATH } from "../../domain/paths";

/**
 * The shell for pages nobody is signed in to.
 *
 * Imports nothing from the authenticated app shell on purpose: that chrome
 * assumes a therapist, a sidebar and a session, none of which exist here.
 */
export default function PublicLayout({ children }: { children: ReactNode }) {
    const { t } = useTranslation();

    return (
        <div className="min-h-screen bg-surface-base text-content-primary flex flex-col">
            <header className="border-b border-gray-200 dark:border-gray-800">
                <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <Link to={DIRECTORY_PATH} className="shrink-0">
                        {/* Same treatment the patient portal gives it: the mark
                            is dark ink, so it is inverted rather than swapped
                            for a second asset. */}
                        <img
                            src="/images/logo/logo.png"
                            alt="Alchely"
                            className="h-8 w-auto dark:brightness-0 dark:invert"
                        />
                    </Link>
                    <div className="flex items-center gap-2">
                        {/* A visitor arriving from a shared link may not read
                            the language the therapist writes in. */}
                        <LanguageSelector />
                        <Link
                            to="/login"
                            className="rounded-full border border-gray-200 dark:border-gray-800 px-4 py-1.5 text-theme-sm text-content-secondary hover:text-content-primary"
                        >
                            {t("publicDirectory.imATherapist")}
                        </Link>
                    </div>
                </div>
            </header>

            <main className="flex-1 mx-auto w-full max-w-5xl px-4 sm:px-6 py-8 sm:py-10">
                {children}
            </main>

            <footer className="border-t border-gray-200 dark:border-gray-800">
                <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 py-6 flex flex-wrap gap-x-6 gap-y-2 text-theme-xs text-content-tertiary">
                    <span>{t("publicDirectory.footerNote")}</span>
                    <Link to="/privacy" className="hover:text-content-secondary">
                        {t("publicDirectory.privacy")}
                    </Link>
                    <Link to="/terms" className="hover:text-content-secondary">
                        {t("publicDirectory.terms")}
                    </Link>
                </div>
            </footer>
        </div>
    );
}
